import { formatDistance } from "date-fns";
import { es } from "date-fns/locale";
import { Notebook, FileText } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useGetList, useGetIdentity, useTranslate } from "ra-core";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ThemeToggle } from "../layout/ThemeToggle";
import { Markdown } from "../misc/Markdown";

/**
 * NotesList — vista top-level de Notas estilo P3.
 *
 * Atomic CRM tiene `contact_notes` y `deal_notes` como tablas separadas
 * (cada nota pertenece a un contacto O a una oportunidad). Aquí las
 * fusionamos en una sola feed cronológico.
 *
 * Toggle "Solo mis notas":
 *   - Admin: OFF por default → ve notas de todos
 *   - Non-admin: ON por default → ve solo las propias
 */
export function NotesList() {
  const { identity, isPending: identityPending } = useGetIdentity();
  const translate = useTranslate();

  const isAdmin = (identity as any)?.administrator === true;
  const [onlyMine, setOnlyMine] = useState<boolean | null>(null);
  useEffect(() => {
    if (identityPending) return;
    if (onlyMine === null) setOnlyMine(!isAdmin);
  }, [identityPending, isAdmin, onlyMine]);

  const filterByMe =
    onlyMine && identity?.id ? { sales_id: identity.id } : undefined;

  const { data: contactNotes, isPending: l1 } = useGetList(
    "contact_notes",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "date", order: "DESC" },
      filter: filterByMe,
    },
    { enabled: onlyMine !== null },
  );
  const { data: dealNotes, isPending: l2 } = useGetList(
    "deal_notes",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "date", order: "DESC" },
      filter: filterByMe,
    },
    { enabled: onlyMine !== null },
  );

  const allNotes = [
    ...(contactNotes ?? []).map((n: any) => ({
      ...n,
      kind: "contact" as const,
    })),
    ...(dealNotes ?? []).map((n: any) => ({ ...n, kind: "deal" as const })),
  ]
    .sort((a, b) => new Date(b.date).valueOf() - new Date(a.date).valueOf())
    .slice(0, 50);

  const loading = identityPending || onlyMine === null || l1 || l2;
  const title = onlyMine ? "Mis notas" : "Todas las notas";

  return (
    <div className="flex flex-col min-h-screen">
      <header className="px-7 pt-6 pb-4 flex items-center gap-3.5 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div
            className="grid place-items-center"
            style={{
              width: 38,
              height: 38,
              borderRadius: 12,
              background: "var(--musco-panel2)",
              color: "var(--musco-text)",
            }}
          >
            <Notebook size={18} />
          </div>
          <div>
            <h1 className="m-0 text-[28px] font-bold tracking-[-0.025em]">
              {title}
            </h1>
            <div className="text-sm" style={{ color: "var(--musco-muted)" }}>
              {allNotes.length} notas — incluye notas de contactos y
              oportunidades
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
            style={{
              background: "var(--musco-panel)",
              boxShadow: "var(--musco-shadow)",
            }}
          >
            <Switch
              id="notes-only-mine"
              checked={!!onlyMine}
              onCheckedChange={(v) => setOnlyMine(v)}
              disabled={identityPending}
            />
            <Label
              htmlFor="notes-only-mine"
              className="cursor-pointer text-sm whitespace-nowrap"
            >
              Solo mis notas
            </Label>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <div className="flex-1 overflow-auto px-7 pb-7 pt-1">
        {loading ? (
          <div
            className="musco-panel p-6 text-sm"
            style={{ color: "var(--musco-muted)" }}
          >
            Cargando notas…
          </div>
        ) : allNotes.length === 0 ? (
          <div className="musco-panel p-10 text-center">
            <FileText
              size={28}
              style={{ color: "var(--musco-muted)", margin: "0 auto 8px" }}
            />
            <p className="text-sm" style={{ color: "var(--musco-muted)" }}>
              {onlyMine
                ? "Aún no tienes notas tuyas. Crea una desde la ficha de un contacto u oportunidad."
                : "Aún no hay notas en el CRM."}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3.5">
            {allNotes.map((note: any) => (
              <NoteRow
                key={`${note.kind}-${note.id}`}
                note={note}
                mine={note.sales_id === identity?.id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Tiny use to keep `translate` lint-quiet (used implicitly by future i18n)
  void translate;
}

function NoteRow({ note, mine }: { note: any; mine: boolean }) {
  const ago = formatDistance(new Date(note.date), new Date(), {
    addSuffix: true,
    locale: es,
  });
  const refUrl =
    note.kind === "contact"
      ? `/contacts/${note.contact_id}/show`
      : `/deals/${note.deal_id}/show`;
  const refLabel = note.kind === "contact" ? "Contacto" : "Oportunidad";
  return (
    <div className="musco-panel p-5">
      <div
        className="flex items-center gap-2 text-xs mb-2"
        style={{ color: "var(--musco-muted)" }}
      >
        <span className="musco-pill">{refLabel}</span>
        <Link
          to={refUrl}
          className="font-semibold"
          style={{ color: "var(--musco-accent2)", textDecoration: "none" }}
        >
          Ver ficha →
        </Link>
        <span className="ml-auto">
          {mine ? "Tú · " : ""}
          {ago}
        </span>
      </div>
      <div className="text-sm leading-[1.55]">
        <Markdown>{note.text || "(sin texto)"}</Markdown>
      </div>
    </div>
  );
}
